import { z } from "zod";

import { compareCivilDate, localDateToCivilDate, parseBrCivilDate } from "@/lib/civil-date";
import { getTomorrowInBrazil } from "@/lib/dates";
import {
  EVENT_FORM_COPY,
  eventLocationField,
  eventNameFieldsSchema,
  guestCountField,
  guestCountValue,
  welcomeMessageField,
} from "@/lib/event-form/event-form";
import { HOST_TABS } from "@/lib/host-panel/host-tabs";
import { ONBOARDING_ENTRY } from "@/lib/onboarding/onboarding-intent";

/**
 * The host's list creation (Nova Lista, `Nova Lista - Interno.dc.html`, adapted to the host by
 * the person's ruling of 2026-09-30): the event, then the curation, and the list is born as a
 * draft the host publishes from Minha Lista.
 *
 * The curation opens, as the internal flow's does (the catalog's `?voltou=sugestao`), on the
 * choice of how the list starts: one of the house's suggestion lists, or the catalog from zero.
 * A host who chose a suggestion before signing up skips that choice.
 *
 * What the internal flow has and the host does not: the client step (the host is the client),
 * the store and the list's administrator, the event type (the MVP is a wedding, and nobody
 * chooses), the review, and the terms invitation the consultant sends — the host accepts the
 * terms here, on their own screen, right after the event (§5.6).
 */

export const CREATE_LIST_HREF = "/anfitriao/nova-lista";
/** The terms, after the event and before the pieces (§5.6). */
export const CREATE_LIST_TERMS_HREF = "/anfitriao/nova-lista/termos";
/** "Como a sua lista começa" (§5.7): the catalog from zero, or a suggestion list. */
export const CREATE_LIST_STARTING_POINT_HREF = "/anfitriao/nova-lista/ponto-de-partida";
export const CREATE_LIST_CURATION_HREF = "/anfitriao/nova-lista/curadoria";
/** The choice of how the list starts, and each suggestion seen from inside the creation. */
export const CREATE_LIST_SUGGESTIONS_HREF = "/anfitriao/nova-lista/listas-de-sugestao";

/** Minha Lista: where someone who already has a list is going. */
const HOST_LIST_TAB_HREF = HOST_TABS.find((tab) => tab.key === "lista")!.href;

/**
 * Where "Adicionar à minha lista" leads someone the page could not read as a host: the
 * onboarding, and from there **their own list**.
 *
 * `/anfitriao/lista` and not the list creation, which is the defect this fixes. A host whose
 * session had merely expired was sent to create a list they already had, and stayed there — the
 * creation only bounces to the panel for a list that is already published, so a draft kept them
 * on the wizard (reported 2026-10-07).
 *
 * One destination answers both people, because the rules for that already exist and disagree
 * with each other nowhere:
 *
 * - **has a list** → Minha Lista, which is where they were going;
 * - **has none** → the panel itself redirects to the creation (`panel-request.ts`,
 *   `NO_LIST_REDIRECT`);
 * - **is signing up now** → a list whose terms are still pending collapses the destination to
 *   the creation anyway (`listCreationDestination`), which is right for a list that does not
 *   exist yet.
 *
 * `returnTo` brings the person back to the page they were reading instead of to the panel — the
 * piece, with its context, so the host CTA is there when they arrive. It has to prefix-match
 * `CUSTOMER_ALLOWED_PREFIXES` (`sanitize-redirect.ts`) or it is dropped for the default.
 */
export function addToMyListHref(returnTo?: string): string {
  const next = returnTo === undefined || returnTo.trim() === "" ? HOST_LIST_TAB_HREF : returnTo;

  return `${ONBOARDING_ENTRY.host}?next=${encodeURIComponent(next)}`;
}

/**
 * Where accepting the terms leads, inside the creation.
 *
 * Two people reach that screen, and they are not in the same place:
 *
 * - someone whose list is **still being built** carries on building it;
 * - someone whose list a consultant already built — event filled, pieces chosen — has nothing
 *   left to create, and goes to **Minha Lista**, where publishing lives.
 *
 * The second case was broken: the terms screen only called `router.refresh()`, and the page then
 * rendered the event step, so the client of a consultant-prepared list redid the event and the
 * curation of a list that was already done (measured 2026-10-08;
 * `docs/handoff-anfitriao-convite-consultora.md` §4 had predicted it). Nothing told the two
 * apart because nothing asked.
 *
 * The question is answered with the rule that already exists: "would the panel let this list
 * in?" — a name, a date and at least one piece, which is exactly what `(painel)/layout.tsx`
 * checks before it lets anyone through. Minha Lista and not Meu Painel because a draft has no
 * Meu Painel tab (`hostTabAccess`), and Minha Lista is where its publishing happens.
 */
export function afterCreateListTermsHref(draft: {
  personName1: string;
  eventDate: string;
  itemCount: number;
}): string {
  const isReady = draft.personName1.trim() !== "" && draft.eventDate.trim() !== "" && draft.itemCount > 0;

  if (isReady) return HOST_LIST_TAB_HREF;

  // Still being built: on to the pieces, by the same rule the event step's own exit follows.
  return curationEntryHref({ itemCount: draft.itemCount, suggestionSlug: null });
}

export function createListSuggestionHref(slug: string): string {
  return `${CREATE_LIST_SUGGESTIONS_HREF}/${encodeURIComponent(slug)}`;
}

/**
 * Where the event step leads. A suggestion only ever starts an empty list (`adoptSuggestion`),
 * so the question of how the list starts is asked while it has no piece and none was chosen
 * yet; a chosen one travels on to the curation, and a curation already begun is simply resumed.
 *
 * The empty case opens the **Ponto de partida** (§5.7) and no longer the grid of suggestions:
 * the host used to land straight on the grid, with the catalog offered as a dashed box beneath
 * it, which made "start from zero" read like the fallback of a choice nobody had been asked.
 */
export function curationEntryHref({ itemCount, suggestionSlug }: { itemCount: number; suggestionSlug: string | null }): string {
  if (suggestionSlug !== null) return withSuggestion(CREATE_LIST_CURATION_HREF, suggestionSlug);

  return itemCount > 0 ? CREATE_LIST_CURATION_HREF : CREATE_LIST_STARTING_POINT_HREF;
}

/** The step bar: the host's three steps, as the design's own `.stepline` counts them. */
export const CREATE_LIST_STEPS = ["Evento", "Ponto de partida", "Curadoria"] as const;
export type CreateListStep = (typeof CREATE_LIST_STEPS)[number];

/**
 * The step's own words. The **field** words are not here: they come from `lib/event-form`, the
 * single source the host's wizard, the panel and the store flow now share.
 */
export const CREATE_LIST_MESSAGE = {
  emptyCuration: "Escolha ao menos uma peça: a lista não pode nascer vazia.",
} as const;

function isFromTomorrowInBrazil(value: string): boolean {
  const parts = parseBrCivilDate(value);
  if (parts === null) return false;

  return compareCivilDate(parts, localDateToCivilDate(getTomorrowInBrazil())) >= 0;
}

/**
 * The event step — the seven fields the design asks for, in the words `lib/event-form` keeps.
 *
 * It used to ask for four things: the couple in **two** fields ("Nome e sobrenome de quem
 * recebe"), the date and the place. The design, in both packages of 2026-10-07, asks for name
 * and surname **separately** for each party, the list's name read-only beneath them, and the
 * welcome message — which lived only in the panel, so a list born here opened with no greeting
 * at all until its host went looking for the field.
 *
 * `savedEventDate` is the date the draft already carries, always accepted: the floor applies to
 * a date being chosen, never to one the list already told.
 */
export function buildEventStepSchema(savedEventDate = "") {
  return z.object({
    ...eventNameFieldsSchema(),
    welcomeMessage: welcomeMessageField(),
    guestCount: guestCountField(),
    eventDate: z
      .string()
      .transform((value) => value.trim())
      .pipe(
        z
          .string()
          .min(1, EVENT_FORM_COPY.errors.eventDate)
          .refine((value) => parseBrCivilDate(value) !== null, EVENT_FORM_COPY.errors.eventDate)
          .refine(
            (value) => value === savedEventDate.trim() || isFromTomorrowInBrazil(value),
            EVENT_FORM_COPY.errors.eventDateInPast,
          ),
      ),
    eventLocation: eventLocationField(),
  });
}

export type EventStepDraft = z.infer<ReturnType<typeof buildEventStepSchema>>;

/**
 * The BFF's body: the same shape, the floor left to the page that knows the saved date.
 *
 * `guestCount` is the **number** the step produced, not the text it read — the step posts its
 * parsed draft. Demanding the text here is what made every submit answer 400 (`guestCountValue`).
 */
export const eventStepBodySchema = z.object({
  ...eventNameFieldsSchema(),
  welcomeMessage: welcomeMessageField(),
  guestCount: guestCountValue(),
  eventDate: z.string().trim().refine((value) => parseBrCivilDate(value) !== null),
  eventLocation: eventLocationField(),
  suggestionSlug: z.string().trim().min(1).max(120).nullable().optional(),
});



/** The curation's count line (`curCount`). */
export function curationCountLabel(count: number): string {
  if (count === 0) return "Nenhuma peça escolhida";

  return `${count} ${count === 1 ? "peça escolhida" : "peças escolhidas"}`;
}

/** The curation's goodbye (`concluirCuradoria`): "3 peças salvas na sua lista". */
export function curationSavedLabel(count: number): string {
  return `${count} ${count > 1 ? "peças salvas" : "peça salva"} na sua lista`;
}

/** Where the chosen suggestion travels: `?sugestao=<slug>`, nothing kept in the browser. */
export const SUGGESTION_PARAM = "sugestao";

export function withSuggestion(href: string, slug: string | null | undefined): string {
  if (slug === null || slug === undefined || slug === "") return href;

  return `${href}?${SUGGESTION_PARAM}=${encodeURIComponent(slug)}`;
}
